import { useTranslations } from 'next-intl'
import { useAction } from 'next-safe-action/hooks'
import { useCallback, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { toast } from 'react-toastify'

import {
  verifyAccount as verifyAccountAction,
  TAccountVerificationResponse,
} from '@/entities/account'
import { useServerErrorHandler } from '@/shared/config'

import { ACCOUNT_TOAST_CONTAINER_ID } from '../lib/toast'
import { TAccountForm } from '../model/form'

export const ERROR = {
  NOT_VERIFIED: 'NOT_VERIFIED',
  FAILED: 'FAILED',
  NOT_CHECKED: 'NOT_CHECKED',
}

export const useVerifyAccount = () => {
  const t = useTranslations('deposit_information')

  const [isVerified, setIsVerified] = useState(false)
  const [isFailed, setIsFailed] = useState(false)

  const { setError, setValue } = useFormContext<TAccountForm>()
  const { handleServerError } = useServerErrorHandler()

  const handleNotVerifiedAccount = () => {
    setError('root.accountServerError', {
      type: ERROR.NOT_VERIFIED,
      message: t('error.invalid_account_info'),
    })
    setError('bankName', { type: ERROR.NOT_VERIFIED })
    setError(
      'bankAccount',
      { type: ERROR.NOT_VERIFIED },
      {
        shouldFocus: true,
      },
    )
  }

  const handleAccountVerificationFailed = useCallback(() => {
    setIsFailed(true)
    setError('root.accountServerError', {
      type: ERROR.FAILED,
      message: t('error.verify_account_holder_failed'),
    })
    setError(
      'accountHolder',
      { type: ERROR.FAILED },
      {
        shouldFocus: true,
      },
    )
    toast(t('error.manual_input_required'), {
      containerId: ACCOUNT_TOAST_CONTAINER_ID,
    })
  }, [setError])

  const handleVerifiedAccount = (data: TAccountVerificationResponse) => {
    setIsVerified(true)
    setValue('accountHolder', data.accountHolderName!)
    toast(
      t('toast.account_holder_verified', {
        accountHolderName: data.accountHolderName ?? '',
      }),
      {
        containerId: ACCOUNT_TOAST_CONTAINER_ID,
      },
    )
  }

  const handleSuccess = (data: TAccountVerificationResponse) => {
    if (!data.success) {
      handleAccountVerificationFailed()

      return
    }

    if (!data.verified) {
      handleNotVerifiedAccount()

      return
    }

    handleVerifiedAccount(data)
  }

  const action = useAction(verifyAccountAction, {
    onSuccess: ({ data }) => {
      if (data) {
        handleSuccess(data)
      }
    },
    onError: ({ error }) => {
      if (error.serverError) {
        handleServerError(error.serverError)
      }
    },
  })

  const markAsUnverified = useCallback(() => {
    setIsVerified(false)
    setIsFailed(false)
  }, [])

  return {
    isVerified,
    isFailed,
    action,
    markAsUnverified,
    handleAccountVerificationFailed,
  }
}
